import { useEffect, useState } from "react";

/**
 * Lets a horizontally scrolling row be grabbed and dragged with the mouse (with a
 * little glide when let go). Touch screens already scroll natively, so only the
 * mouse is handled. A drag never counts as a click on the items inside.
 * Give the element the "drag-scroll" class (see index.css) for the hand cursor.
 * Use the returned value as the element's ref (it works for rows that mount later).
 */
export function useDragScroll() {
  const [el, ref] = useState(null);

  useEffect(() => {
    if (!el) return undefined;
    let down = false, moved = false, startX = 0, startLeft = 0, lastX = 0, lastT = 0, vel = 0, raf = 0, dragEnd = 0;

    const onDown = (e) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      cancelAnimationFrame(raf);
      down = true; moved = false; vel = 0;
      startX = lastX = e.clientX;
      startLeft = el.scrollLeft;
      lastT = performance.now();
    };
    const onMove = (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 5) { moved = true; el.classList.add("is-dragging"); }
      if (!moved) return;
      el.scrollLeft = startLeft - dx;
      const now = performance.now();
      const dt = now - lastT;
      if (dt > 0) vel = vel * 0.7 + ((lastX - e.clientX) / dt) * 0.3;
      lastX = e.clientX; lastT = now;
    };
    const onUp = () => {
      if (!down) return;
      down = false;
      el.classList.remove("is-dragging");
      if (!moved) return;
      dragEnd = performance.now();
      const glide = () => {
        if (Math.abs(vel) < 0.03) return;
        el.scrollLeft += vel * 16;
        vel *= 0.94;
        raf = requestAnimationFrame(glide);
      };
      glide();
    };
    const onClick = (e) => {
      if (performance.now() - dragEnd < 250) { e.preventDefault(); e.stopPropagation(); }
    };

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("click", onClick, true);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("click", onClick, true);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [el]);

  return ref;
}
